import PropTypes from 'prop-types';
import {useEffect, useState} from 'react';
import {useImageOptimizer} from '~/hooks/useImageOptimizer';

/* wrapper to allow useImageOptimizer hook within server components
 * returns an image tag or a div with an optimized background image
 */

export function ResponsiveImageWrapper({
  image = null,
  responsiveImage,
  elementType = 'img',
  classNames,
  imageAlt,
  anchor,
  initialWindowWidth = 700,
  children,
}) {
  const [currentResponsiveImage, setCurrentResponsiveImage] = useState(image);
  const [windowWidth, setWindowWidth] = useState(initialWindowWidth);

  useEffect(() => {
    const handleResize = () => {
      if (!image && !responsiveImage) return;
      const width = window.innerWidth;
      setWindowWidth(window.innerWidth);

      if (width < 769 && responsiveImage?.mobileImage?.asset) {
        setCurrentResponsiveImage(responsiveImage?.mobileImage);
      } else {
        setCurrentResponsiveImage(image);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, [image, responsiveImage]);

  const optimizedImage = useImageOptimizer(
    currentResponsiveImage ? currentResponsiveImage?.asset?.url : '',
    windowWidth,
  );

  if (!image && !responsiveImage) return null;
  const content =
    'img' === elementType ? (
      <img
        src={optimizedImage?.optimizedImageSrc ?? ''}
        alt={imageAlt ?? ''}
        className={classNames}
        loading="lazy"
      />
    ) : 'div' === elementType ? (
      <div
        className={classNames}
        style={{
          backgroundImage: `url('${optimizedImage?.optimizedImageSrc ?? ''}')`,
        }}
      >
        {children}
      </div>
    ) : (
      <a
        className={classNames}
        href={anchor?.href}
        target={anchor?.target}
        rel={anchor?.rel}
        style={{
          backgroundImage: `url('${optimizedImage?.optimizedImageSrc ?? ''}')`,
        }}
      >
        {children}
      </a>
    );

  return content;
}

ResponsiveImageWrapper.propTypes = {
  image: PropTypes.object,
  responsiveImage: PropTypes.object,
  elementType: PropTypes.oneOf(['img', 'div', 'a']),
  classNames: PropTypes.string,
  imageAlt: PropTypes.string,
  children: PropTypes.any,
  anchor: PropTypes.object,
  initialWindowWidth: PropTypes.number,
};
